
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isInRecoveryMode } from '@/utils/deployment/recoveryUtils';
import { getStorageUsage } from '@/utils/deployment/storageQuotaManager';
import StorageHealthIndicator from '@/components/system/StorageHealthIndicator';

/**
 * LayoutDeploymentTracker - A lightweight component for tracking deployment status
 * Optimized for minimal performance impact
 */
const LayoutDeploymentTracker: React.FC = () => {
  const location = useLocation();
  const mountTimestamp = useRef(Date.now());
  const showStorageHealthRef = useRef(false);
  
  // Check recovery mode on mount
  useEffect(() => {
    try {
      // Only show storage health indicator if needed
      const recoveryMode = isInRecoveryMode();
      if (recoveryMode) {
        showStorageHealthRef.current = true;
        return;
      }
      
      // Check storage usage
      const { percentage } = getStorageUsage();
      showStorageHealthRef.current = percentage > 0.8;
    } catch (e) {
      // Silent fail
    }
    
    return () => {
      console.log('Layout tracker unmounted');
    };
  }, []);

  return (
    <>
      {/* Hidden element for tracking */}
      <div 
        id={`layout-debug-${mountTimestamp.current}`} 
        data-render-path={location.pathname}
        className="hidden" 
        aria-hidden="true"
      />
      
      {/* Storage health indicator - only shown when needed */}
      {showStorageHealthRef.current && (
        <div className="fixed bottom-4 right-4 z-50">
          <StorageHealthIndicator />
        </div>
      )}
    </>
  );
};

export default React.memo(LayoutDeploymentTracker);
